import { useEffect, useState } from 'react';
import { TokenService } from '../services/token.service';
import {
  Voice,
  VoiceGroup,
  VoiceProvider,
  VoiceProviderInfo,
  generateSpeech,
  getAudioHistory,
  getVoiceProviders,
  getVoices
} from '../services/voiceService';

// Audio History interface
interface AudioFile {
  _id: string;
  text: string;
  voiceId: string;
  provider: VoiceProvider;
  localPath: string;
  style?: string;
  createdAt: string;
  expiresAt: string;
}

// Interfaces
interface UseTextToSpeechReturn {
  text: string;
  setText: (text: string) => void;
  voice: string;
  setVoice: (voice: string | null) => void;
  provider: VoiceProvider;
  setProvider: (provider: VoiceProvider) => void;
  providers: VoiceProviderInfo[];
  voices: VoiceGroup[];
  isLoadingProviders: boolean;
  isLoadingVoices: boolean;
  isGenerating: boolean;
  audioUrl: string | null;
  handleGenerate: () => Promise<void>;
  currentVoiceDetails: Voice | null;
  insufficientTokens: boolean;
  requiredTokens: number;
  availableTokens: number;
  clearTokenError: () => void;
  showBuyTokensModal: boolean;
  setShowBuyTokensModal: (show: boolean) => void;
  audioHistory: AudioFile[];
  isLoadingHistory: boolean;
  fetchAudioHistory: () => Promise<void>;
}

/**
 * Hook for Text-to-Speech functionality with multiple providers
 */
export function useTextToSpeech(): UseTextToSpeechReturn {
  const [text, setText] = useState<string>('');
  const [voice, setVoice] = useState<string>('');
  const [provider, setProvider] = useState<VoiceProvider>(VoiceProvider.SERVERT);
  const [providers, setProviders] = useState<VoiceProviderInfo[]>([]);
  const [voices, setVoices] = useState<VoiceGroup[]>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState<boolean>(false);
  const [isLoadingVoices, setIsLoadingVoices] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [currentVoiceDetails, setCurrentVoiceDetails] = useState<Voice | null>(null);
  const [userTokens, setUserTokens] = useState<number>(0);
  const [insufficientTokens, setInsufficientTokens] = useState<boolean>(false);
  const [requiredTokens, setRequiredTokens] = useState<number>(0);
  const [showBuyTokensModal, setShowBuyTokensModal] = useState<boolean>(false);
  const [audioHistory, setAudioHistory] = useState<AudioFile[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);

  // Fetch audio history
  const fetchAudioHistory = async () => {
    try {
      setIsLoadingHistory(true);
      const result = await getAudioHistory();
      setAudioHistory(result.audioFiles || []);
    } catch (error) {
      console.error('Error fetching audio history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Fetch voice providers
  async function fetchProviders() {
    try {
      setIsLoadingProviders(true);
      const data = await getVoiceProviders();
      setProviders(data);
      if (data.length > 0 && !provider) {
        setProvider(data[0].id as VoiceProvider);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setIsLoadingProviders(false);
    }
  }

  // Fetch voices for the selected provider
  async function fetchVoices() {
    try {
      setIsLoadingVoices(true);
      const data = await getVoices(provider);
      setVoices(data);
      
      // If there's no voice selected, select the first one
      if (!voice && data.length > 0 && data[0].voices.length > 0) {
        setVoice(data[0].voices[0].id);
        setCurrentVoiceDetails(data[0].voices[0]);
      }
    } catch (error) {
      console.error('Error fetching voices:', error);
    } finally {
      setIsLoadingVoices(false);
    }
  }

  // Fetch token balance
  async function fetchTokenBalance() {
    try {
      const data = await TokenService.getUserToken();
      setUserTokens(data.balance);
    } catch (error) {
      console.error('Error fetching token balance:', error);
    }
  }

  // Effect to fetch audio history on mount
  useEffect(() => {
    fetchAudioHistory();
  }, []);

  // Effect to fetch providers and token balance on mount
  useEffect(() => {
    fetchProviders();
    fetchTokenBalance();
  }, []);

  // Effect to fetch voices when provider changes
  useEffect(() => {
    if (provider) {
      fetchVoices();
    }
  }, [provider]);

  // Handle voice change
  const handleVoiceChange = (voiceId: string | null) => {
    if (!voiceId) return;
    
    setVoice(voiceId);
    
    // Find and set the current voice details
    for (const group of voices) {
      const found = group.voices.find(v => v.id === voiceId);
      if (found) {
        setCurrentVoiceDetails(found);
        break;
      }
    }
  };

  // Handle generate speech
  const handleGenerate = async () => {
    if (!text || !voice) return;
    
    try {
      setIsGenerating(true);
      setAudioUrl(null);
            
      const result = await generateSpeech({
        text,
        voiceId: voice,
        provider,
        // Add additional parameters as needed
      });
                  
      // Ensure result is string and set as audio URL
      const audioUrlResult = result as string;
      setAudioUrl(audioUrlResult);
      setIsGenerating(false);
      
      setText('');
      fetchTokenBalance();
      fetchAudioHistory();
    } catch (err) {
      console.error('Error generating speech:', err);
      setIsGenerating(false);
    }
  };

  const clearTokenError = () => {
    setInsufficientTokens(false);
  };

  return {
    text,
    setText,
    voice,
    setVoice: handleVoiceChange,
    provider,
    setProvider,
    providers,
    voices,
    isLoadingProviders,
    isLoadingVoices,
    isGenerating,
    audioUrl,
    handleGenerate,
    currentVoiceDetails,
    insufficientTokens,
    requiredTokens,
    availableTokens: userTokens,
    clearTokenError,
    showBuyTokensModal,
    setShowBuyTokensModal,
    audioHistory,
    isLoadingHistory,
    fetchAudioHistory
  };
} 